// "Enviar para o My Planner?" (texto 10, E1/E2): ao criar um item com data em qualquer aba, uma pergunta gentil;
// "Me lembre depois" deixa o item pendente e o app lembra (dentro do app) na 1ª abertura do dia e na 1ª depois das 17h.
// Regra fixa: nada vai sozinho para o My Planner. "Enviar agora" abre a página que já existe (a ficha da tarefa, com
// dia e horário), já preenchida. A tela que mostra tudo isso é components/EnvioPlanner.tsx (montada na raiz do app).
import { useSyncExternalStore } from 'react';
import type { AbaId, ModId, Task } from '../store/types';
import { MOD_IDS } from '../store/types';
import { getDoc, setDoc } from '../store/store';
import { mudarReg, regPorId, regsDe } from '../store/modulos';
import { warrantyEnd } from '../store/warranty';
import { iconeDoc } from '../store/documentos';
import { DEFS } from '../modulos/defs';
import { ctxDe } from '../modulos/ctx';
import { listaNoPlanner } from './enviarPlanner';
import { chaveEnvio, encerrarPendente, escolhaDaAba, marcarPendente, mudarPendente, paraPlanner, pendentes } from './envioEstado';
import { noNaoPerturbe } from './avisos';
import { addDays, brYmd, today } from './date';
import { t } from './i18n';
import { Alert } from 'react-native';
import { newTask, saveTask } from '../store/planner';

/** O item recém-criado. abrir: a página "Colocar no My Planner" da própria tela (se houver); sem ela, a ficha da tarefa já preenchida. */
export type PedidoEnvio = { aba: AbaId; id: string; titulo: string; emoji?: string; data?: string | null; hora?: string | null; prof?: string;
  lista?: string; abrir?: () => void };
type Fila = { p: PedidoEnvio; direto?: boolean } | null;

const ehMod = (a: AbaId): a is ModId => (MOD_IDS as string[]).includes(a);

// ─── a pergunta (E1) ───
let fila: Fila = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach(f => f());
export const usePedidoEnvio = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => { ouvintes.delete(cb); }; }, () => fila, () => null);
export const fecharPedidoEnvio = () => { fila = null; avisar(); };

/** Chamar logo depois de CRIAR um item que tem data (com ou sem horário). Não pergunta se:
 *  a data já passou; a lista do item já foi enviada inteira ao My Planner; o item já está no My Planner;
 *  ou a pessoa pediu "Lembrar minha escolha nesta aba". */
export function perguntarEnvio(p: PedidoEnvio) {
  if (!p.data || !/^\d{4}-\d{2}-\d{2}/.test(p.data) || p.data.slice(0, 10) < today()) return;
  if (p.lista && listaNoPlanner(getDoc('ajustes'), p.aba, p.lista)) return;
  if (getDoc('planner').tasks.some(x => x.link?.aba === p.aba && x.link?.id === p.id)) return;   // consulta que já vai sozinha, remédio...
  if (ehMod(p.aba) && regPorId(p.aba, p.id)?.tarefa) return;
  const e = escolhaDaAba(p.aba);
  if (e === 'nao') return;
  if (e === 'depois') return depois(p);
  fila = { p, direto: e === 'enviar' }; avisar();
}

export function depois(p: PedidoEnvio) {
  marcarPendente({ aba: p.aba, id: p.id, titulo: p.titulo, emoji: p.emoji, data: p.data ?? null, hora: p.hora ?? null, ...(p.prof ? { prof: p.prof } : {}) });
}

type AbrirFicha = (a: { date?: string; prefill?: Partial<Task>; onSaved?: (tk: Task) => void }) => void;
/** "Enviar agora": a página da própria tela ou a ficha da tarefa preenchida (o mesmo que "Colocar no My Planner"). */
export function enviarAgora(p: PedidoEnvio, abrirFicha: AbrirFicha) {
  encerrarPendente(chaveEnvio(p.aba, p.id));
  if (p.abrir) return p.abrir();
  const pre = prefillDe(p);
  if (!pre) return;
  abrirFicha({ date: pre.start, prefill: pre, onSaved: tk => { if (ehMod(p.aba)) mudarReg(p.aba, p.id, x => ({ ...x, tarefa: tk.id })); } });
}

/** Lembrete de renovação de um documento (o mesmo do ⋮ "Lembrete de renovação no Planner"). */
export function prefillRenovacao(d: { id: string; tipo: string; nome?: string; validade: string | null; orgao?: string }): Partial<Task> {
  const nome = d.nome?.trim() || t(d.tipo);
  return {
    title: t('Renovar: {nome}', { nome }), emoji: iconeDoc(d.tipo), emojiManual: true, area: 'documentos', energy: 2,
    start: [60, 30, 15, 7].map(n => addDays(d.validade!, -n)).find(x => x >= today()) ?? today(),
    link: { aba: 'documentos', id: d.id, label: nome },
    notes: t('Vence em {data}.', { data: brYmd(d.validade!) }) + (d.orgao ? ' ' + t('Órgão emissor: {nome}', { nome: d.orgao }) : ''),
  };
}

/** O que vai para a ficha da tarefa, lido do item como está agora (E4: nas abas de saúde só título, horário e profissional). */
export function prefillDe(p: PedidoEnvio): Partial<Task> | null {
  if (ehMod(p.aba)) {
    const r = regPorId(p.aba, p.id);
    if (!r) return null;
    const col = DEFS[p.aba].colecoes.find(c => c.id === r.col);
    if (!col) return null;
    const ctx = ctxDe(p.aba, regsDe(p.aba));
    const bruto = col.agenda?.(r, ctx) ?? {};
    const ag = Object.fromEntries(Object.entries(bruto).filter(([, v]) => v !== undefined)) as Partial<Task>;
    const titulo = col.titulo(r, ctx) || p.titulo;
    const data = (col.data ? r.c[col.data] : null) || col.campos.filter(f => f.t === 'data' && !!f.aviso?.length).map(f => String(r.c[f.k] ?? '')).find(Boolean) || p.data || today();
    const emoji = typeof col.icone === 'function' ? col.icone(r) : col.icone;
    return paraPlanner(p.aba, p.id, { area: p.aba, emojiManual: true, emoji, title: titulo, start: data, time: p.hora ?? null,
      link: { aba: p.aba, id: r.id, label: titulo }, ...ag, notes: String(ag.notes ?? '') }, profDe(p.aba, r.c));
  }
  if (p.aba === 'documentos') {
    const d = getDoc('documentos').items.find(x => x.id === p.id);
    return d?.validade ? prefillRenovacao(d) : null;
  }
  if (p.aba === 'garantias') {
    const w = getDoc('garantias').items.find(x => x.id === p.id);
    if (!w) return null;
    const fim = warrantyEnd(w);
    return { title: w.name, emoji: '🧾', emojiManual: true, area: 'garantias', start: fim, link: { aba: 'garantias', id: w.id, label: w.name },
      notes: t('Garantia até {data}.', { data: brYmd(fim) }) };
  }
  return paraPlanner(p.aba, p.id, { title: p.titulo, ...(p.emoji ? { emoji: p.emoji, emojiManual: true } : {}), start: p.data || today(), time: p.hora ?? null, area: p.aba }, p.prof);
}

/** Nome do médico ou profissional (E4), quando houver. */
export function profDe(aba: AbaId, c: Record<string, any>): string | undefined {
  if (aba === 'saude') return String(c.medico ?? '').trim() || undefined;
  return undefined;
}

// ─── o aviso dentro do app (E2) ───
let aviso: string[] = [];
const ouvAviso = new Set<() => void>();
export const useAvisoEnvio = () => useSyncExternalStore(cb => { ouvAviso.add(cb); return () => { ouvAviso.delete(cb); }; }, () => aviso, () => aviso);
export const fecharAvisoEnvio = () => { aviso = []; ouvAviso.forEach(f => f()); };

/** Chamado ao abrir o app (e ao voltar para ele). Mostra o aviso na 1ª abertura do dia e na 1ª depois das 17h,
 *  e também quando chega a hora escolhida em "Escolher quando". Cada aparição conta; depois de 3, o item fica só na lista.
 *  No "Não perturbe" do app, não mostra (e não gasta a vez). */
export function conferirAvisoEnvio(agora = new Date()) {
  if (noNaoPerturbe(agora)) return;
  const d = getDoc('envios');
  const dia = today();
  const m = d?.mostrado?.dia === dia ? d.mostrado : { dia };
  const vivos = pendentes().filter(([, p]) => p.vezes < 3 && existe(p.aba, p.id));
  const chegou = vivos.filter(([, p]) => p.quando && new Date(p.quando).getTime() <= agora.getTime());
  const vez: 'manha' | 'tarde' | null = !m.manha ? 'manha' : agora.getHours() >= 17 && !m.tarde ? 'tarde' : null;
  const normais = vez ? vivos.filter(([, p]) => !p.quando) : [];
  if (vez) setDoc('envios', x => ({ ...x, pend: x?.pend ?? {}, mostrado: { ...m, [vez]: true } }));
  const mostrar = [...new Set([...chegou, ...normais].map(([k]) => k))];
  if (!mostrar.length) return;
  for (const k of mostrar) mudarPendente(k, p => ({ ...p, vezes: p.vezes + 1, quando: null }));
  aviso = mostrar; ouvAviso.forEach(f => f());
}

/** O item ainda existe na aba? (excluído: sai dos pendentes) */
export function existe(aba: AbaId, id: string) {
  if (ehMod(aba)) return !!regPorId(aba, id);
  if (aba === 'documentos') return getDoc('documentos').items.some(x => x.id === id);
  if (aba === 'garantias') return getDoc('garantias').items.some(x => x.id === id);
  return true;
}

// ─── "Escolher quando" e a lista de pendentes ───
export function escolherQuando(chave: string, quando: Date) { mudarPendente(chave, p => ({ ...p, quando: quando.toISOString(), vezes: 0 })); }
/** "Me lembre depois" tocado de propósito na lista: volta a aparecer no aviso. */
export const lembrarDepois = (chave: string) => mudarPendente(chave, p => ({ ...p, vezes: 0 }));

let lista = false;
const ouvLista = new Set<() => void>();
export const useListaEnvios = () => useSyncExternalStore(cb => { ouvLista.add(cb); return () => { ouvLista.delete(cb); }; }, () => lista, () => false);
export const abrirListaEnvios = (v = true) => { lista = v; ouvLista.forEach(f => f()); };


// ─── texto 21 (B3): "Selecionar" vários e "Enviar" para o My Planner de uma vez ───
/** Tem data de verdade (sem ela, o item não vai: não inventa o dia de hoje). */
function temData(aba: AbaId, id: string): boolean {
  if (!ehMod(aba)) return true;
  const r = regPorId(aba, id); if (!r) return false;
  const col = DEFS[aba].colecoes.find(c => c.id === r.col); if (!col) return false;
  const ctx = ctxDe(aba, regsDe(aba));
  if (col.agenda?.(r, ctx)?.start) return true;
  if (col.data && r.c[col.data]) return true;
  return col.campos.some(f => f.t === 'data' && !!f.aviso?.length && !!r.c[f.k]);
}
/** Cada item vira uma tarefa ligada a ele (os que já estão no My Planner não repetem). pre: a tarefa já montada (Finanças). */
export function enviarVariosAoPlanner(itens: { aba: AbaId; id: string; titulo: string; pre?: Partial<Task> | null }[]) {
  let n = 0, ja = 0, sem = 0;
  const tarefas = getDoc('planner').tasks;
  for (const x of itens) {
    const chave = x.pre?.link?.id ?? x.id;
    const r = ehMod(x.aba) ? regPorId(x.aba, x.id) : null;
    if (tarefas.some(tk => tk.link?.aba === x.aba && tk.link?.id === chave) || (r?.tarefa && tarefas.some(tk => tk.id === r.tarefa))) { ja++; continue; }
    const pre = x.pre ?? (temData(x.aba, x.id) ? prefillDe({ aba: x.aba, id: x.id, titulo: x.titulo }) : null);
    if (!pre?.start) { sem++; continue; }
    const tk = newTask(pre);
    saveTask(tk);
    if (r) mudarReg(x.aba as ModId, x.id, y => ({ ...y, tarefa: tk.id }));
    encerrarPendente(chaveEnvio(x.aba, x.id));
    n++;
  }
  Alert.alert(t('Enviar para o My Planner'), [n ? t('{n} item(ns) enviado(s).', { n }) : '', ja ? t('{n} já estava(m) no My Planner.', { n: ja }) : '',
    sem ? t('{n} sem data (não foi).', { n: sem }) : ''].filter(Boolean).join('\n') || t('Nada para enviar.'));
}
